import { useMemo, useState } from 'react';
import { ActionIcon, Box, CloseButton, Flex, Stack, Tooltip } from '@mantine/core';
import { useClipboard } from '@mantine/hooks';
import { getCoreRowModel, useReactTable, flexRender } from '@tanstack/react-table';

import Button from '@/components/content/button/Button';
import CreateKeyModal from './CreateKeyModal';

import {
  useGetApiKeys,
  useCreateApiKey,
  useDeleteApiKey,
  usePatchApiKey,
} from '../../services/apikeys';
import { settingsKeys } from '../../services/keys';
import { apiKeysColumns } from './apikeys.table';

import { useConfirmation } from '@/hooks/useConfirmation';
import { useQueryActions } from '@/hooks/useQueryActions';
import { useToast } from '@/hooks/useToast';

import CopyIcon from '@/assets/icons/content/copy.svg';

import type { ApiKeyResponse, ApiKeyCreateResponse, ApiKeysTableMeta, EditingCell } from './types';

interface ApiKeysTabProps {
  userId: number | 'self';
}

const ApiKeysTab = ({ userId }: ApiKeysTabProps) => {
  const { data: apiKeys = [], isLoading, error } = useGetApiKeys(userId);
  const { mutate: createKey, isPending: isCreating } = useCreateApiKey(userId);
  const { mutate: deleteKey } = useDeleteApiKey(userId);
  const { mutate: patchKey } = usePatchApiKey(userId);

  const { invalidateQuery } = useQueryActions();
  const { getConfirmation } = useConfirmation();
  const { createToast } = useToast();
  const clipboard = useClipboard({ timeout: 2000 });

  const [createdKey, setCreatedKey] = useState<(ApiKeyCreateResponse & { name: string }) | null>(
    null
  );
  const [editing, setEditing] = useState<EditingCell | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const queryKey = settingsKeys.apiKeys(userId);

  const handleDelete = async (row: ApiKeyResponse) => {
    const confirmed = await getConfirmation({
      title: 'Delete API Key',
      text: `Are you sure you want to delete API key '${row.name}'? This action is permanent and cannot be undone.`,
      confirmText: 'Delete Key',
      confirmColor: 'var(--quo-pink)',
    });

    if (confirmed) {
      deleteKey(row.id, {
        onSuccess: () => {
          createToast('API key deleted', 'success');
          invalidateQuery(queryKey);
        },
        onError: () => {
          createToast('Failed to delete API key', 'error');
        },
      });
    }
  };

  const handleRenameBlur = (row: ApiKeyResponse) => {
    if (!editing) return;

    const newName = editing.value.trim();
    if (!newName || newName === row.name) {
      setEditing(null);
      return;
    }

    patchKey(
      { keyId: row.id, name: newName },
      {
        onSuccess: () => {
          invalidateQuery(queryKey);
          setEditing(null);
        },
        onError: (err) => {
          const detail = err instanceof Error ? err.message : String(err);
          createToast(`Failed to rename API key: ${detail}`, 'error');
          setEditing(null);
        },
      }
    );
  };

  const handleCreate = (name: string) => {
    createKey(
      { name },
      {
        onSuccess: (data) => {
          setCreatedKey({ ...data, name });
          setCreateModalOpen(false);
          invalidateQuery(queryKey);
        },
        onError: (err) => {
          const detail = err instanceof Error ? err.message : String(err);
          createToast(`Failed to create API key: ${detail}`, 'error');
        },
      }
    );
  };

  const tableMeta: ApiKeysTableMeta = useMemo(
    () => ({
      editing,
      onStartEdit: (key: ApiKeyResponse) => setEditing({ keyId: key.id, value: key.name }),
      onChangeEdit: (keyId: number, value: string) => setEditing({ keyId, value }),
      onBlurEdit: handleRenameBlur,
      onCancelEdit: () => setEditing(null),
      onDelete: handleDelete,
    }),
    [editing, userId]
  );

  const table = useReactTable({
    data: apiKeys,
    columns: apiKeysColumns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => String(row.id),
    meta: tableMeta,
  });

  if (error) {
    return <p>Failed to load API keys.</p>;
  }

  if (isLoading) {
    return <p>Loading API keys...</p>;
  }

  return (
    <Stack gap={15}>
      {createdKey && (
        <Box
          style={{
            background: 'var(--success-message-bg)',
            color: 'var(--success-message-font)',
            padding: '15px',
            borderRadius: '4px',
            position: 'relative',
          }}
        >
          <CloseButton
            size="sm"
            onClick={() => setCreatedKey(null)}
            aria-label="Close"
            style={{ position: 'absolute', top: 8, right: 8 }}
          />
          <Stack gap={10} style={{ paddingRight: 28 }}>
            <span>
              <b>API key &lsquo;{createdKey.name}&rsquo; created successfully.</b>
            </span>
            <Flex
              align="center"
              gap={8}
              style={{
                background: 'rgba(0, 0, 0, 0.08)',
                borderRadius: '4px',
                padding: '4px 8px',
                fontFamily: 'monospace',
                fontSize: '13px',
                wordBreak: 'break-all',
                flex: 1,
                minWidth: 0,
              }}
            >
              <span style={{ flex: 1, minWidth: 0 }}>{createdKey.api_key}</span>
              <Tooltip label={clipboard.copied ? 'Copied' : 'Copy to clipboard'}>
                <ActionIcon
                  size={20}
                  variant="transparent"
                  color="currentColor"
                  onClick={() => clipboard.copy(createdKey.api_key)}
                  aria-label="Copy API key"
                  style={{ flexShrink: 0 }}
                >
                  <CopyIcon width={14} height={14} />
                </ActionIcon>
              </Tooltip>
            </Flex>
            <span>
              After this message is closed, you will not be able to retrieve this key again.
            </span>
          </Stack>
        </Box>
      )}

      {apiKeys.length > 0 && (
        <Box style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      style={{
                        textAlign: 'left',
                        padding: '8px 12px',
                        borderBottom: '1px solid var(--border-color)',
                        width: header.getSize(),
                      }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      style={{
                        padding: '8px 12px',
                        borderBottom: '1px solid var(--border-color)',
                      }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      )}

      {apiKeys.length === 0 && <p>No API keys configured.</p>}

      <Box>
        <Button color="var(--quo-green-50)" onClick={() => setCreateModalOpen(true)}>
          Create Key
        </Button>
      </Box>

      <CreateKeyModal
        opened={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onConfirm={handleCreate}
        isCreating={isCreating}
      />
    </Stack>
  );
};

export default ApiKeysTab;
